import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  TABLE_HEADER_CELL_CLASS,
  type BadgeTone,
} from '@safetyhub/ui';
import { DirtyHint } from '../../../components/common/DirtyHint';
import type { CompanyRbacOverview, LegalPositionGuidance } from '../../../lib/rbac';

/** 선임 필요 여부 배지 — 값 어휘는 shared `LegalPositionGuidance.requirement`. */
const REQUIREMENT_LABELS: Record<LegalPositionGuidance['requirement'], { label: string; tone: BadgeTone }> = {
  required: { label: '선임 필요', tone: 'rose' },
  conditional: { label: '조건부', tone: 'amber' },
  'not-required': { label: '해당 없음', tone: 'slate' },
};

interface LegalGuidanceSectionProps {
  overview: CompanyRbacOverview | null;
  isMobile: boolean;
  isSaving: boolean;
  /** null = 미입력(비움). 저장 응답이 정본이라 호출부가 overview를 갈아 끼운다. */
  onSave: (regularWorkerCount: number | null) => Promise<void>;
}

/** 입력 문자열 → 저장값. 빈 문자열은 null(미입력), 그 외 0 이상 정수만. */
function parseCount(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

/**
 * ① 상시근로자 수 → 선임 안내 표(결정 §6 순서의 첫 섹션).
 *
 * 안내는 **안내 체크리스트**다(결정 §4 — 업종별 예외가 많아 배정을 강제하지 않는다). 표의 값은 전부
 * 서버 `guidance`(저장 응답이 정본)이고, 입력 중 클라 재계산은 하지 않는다.
 */
export function LegalGuidanceSection({ overview, isMobile, isSaving, onSave }: LegalGuidanceSectionProps) {
  const saved = overview?.regularWorkerCount ?? null;
  const [draft, setDraft] = useState<string>(saved === null ? '' : String(saved));

  // 저장 응답·재조회로 정본이 바뀌면 입력을 그 값으로 되맞춘다(편집 중 다른 값을 덮는 경우는 저장 직후뿐).
  useEffect(() => {
    setDraft(saved === null ? '' : String(saved));
  }, [saved]);

  const parsed = parseCount(draft);
  const isValid = parsed !== undefined;
  const isDirty = isValid && parsed !== saved;

  const guidance: LegalPositionGuidance[] = overview?.guidance ?? [];

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-1`}>상시근로자 수와 선임 안내</h2>
      <p className="text-xs text-ink-500 mb-4">
        산업안전보건법상 선임이 필요한 직위를 안내합니다. 업종별 예외가 있어 배정을 강제하지 않습니다.
      </p>

      <form
        className={`flex ${isMobile ? 'flex-col gap-2' : 'items-end gap-3'} mb-5`}
        onSubmit={(e) => {
          e.preventDefault();
          if (!isDirty || isSaving) return;
          void onSave(parsed ?? null);
        }}
      >
        <div className={isMobile ? '' : 'w-48'}>
          <label htmlFor="regular-worker-count" className={FORM_LABEL_CLASS}>
            상시근로자 수
          </label>
          <input
            id="regular-worker-count"
            type="text"
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={FORM_CONTROL_CLASS}
            placeholder="예: 120"
            disabled={isSaving}
            aria-invalid={!isValid}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" size={isMobile ? 'sm' : 'md'} disabled={!isDirty || isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </Button>
          <DirtyHint isDirty={isDirty} className="text-xs" />
        </div>
      </form>
      {!isValid && <p className="-mt-3 mb-4 text-xs text-rose-600">0 이상의 정수를 입력하세요.</p>}

      {guidance.length === 0 ? (
        <p className="text-sm text-ink-500">상시근로자 수를 저장하면 선임이 필요한 직위를 안내합니다.</p>
      ) : isMobile ? (
        <ul className="space-y-2">
          {guidance.map((g) => (
            <li key={g.legalPosition} className="rounded-card border border-line-soft p-3 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-ink-900">{g.label}</span>
                <RequirementBadge requirement={g.requirement} />
              </div>
              <p className="text-xs text-ink-500">{g.article}</p>
              <p className="text-xs text-ink-600">{g.reason}</p>
              <p className="text-xs text-ink-500">현재 보유 {g.holderCount}명</p>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={TABLE_HEADER_CELL_CLASS}>직위</th>
                <th className={TABLE_HEADER_CELL_CLASS}>조문</th>
                <th className={TABLE_HEADER_CELL_CLASS}>필요 여부</th>
                <th className={TABLE_HEADER_CELL_CLASS}>사유</th>
                <th className={TABLE_HEADER_CELL_CLASS}>현재 보유</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-line-soft">
              {guidance.map((g) => (
                <tr key={g.legalPosition}>
                  <td className="px-4 py-3 text-sm font-medium text-ink-900 whitespace-nowrap">{g.label}</td>
                  <td className="px-4 py-3 text-sm text-ink-500 whitespace-nowrap">{g.article}</td>
                  <td className="px-4 py-3 text-sm">
                    <RequirementBadge requirement={g.requirement} />
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-600">{g.reason}</td>
                  <td className="px-4 py-3 text-sm text-ink-700 whitespace-nowrap">{g.holderCount}명</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RequirementBadge({ requirement }: { requirement: LegalPositionGuidance['requirement'] }) {
  const { label, tone } = REQUIREMENT_LABELS[requirement] ?? REQUIREMENT_LABELS['not-required'];
  return (
    <Badge tone={tone} shape="rounded" size="sm">
      {label}
    </Badge>
  );
}
